import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';

import classNames from 'classnames';

import { useIsAuthenticated } from '@azure/msal-react';

import { AppointmentService } from 'features/order-tracker/services/AppointmentService';
import { AppointmentStatusInfo } from 'features/order-tracker/shared-components/AppointmentStatusInfo/AppointmentStatusInfo';
import { ClockIcon, EmailIcon, HomeIcon, PhoneIcon, ProfileIcon } from 'features/order-tracker/shared-components/svg';
import { APPT_USER_PATH_STEP_STORAGE_NAME } from 'features/order-tracker/store/hooks/contstants';
import { getAmendsInfo, getIsAmendAppointmentEnabled } from 'features/order-tracker/store/selectors';
import { APPOINTMENT_TITLE, getLoginButtonText } from 'features/order-tracker/utils/common';
import { APPOINTMENT_STATUSES, OPENREACH_APPOINTMENT_TYPES } from 'features/order-tracker/utils/entries';
import { CONTACT_US_INFO_TYPE } from 'features/order-tracker/utils/entries/contactUsInfoType';

import { MAP_CONTENT_BY_STEP } from '../../../SnapshotCardSection/components/TodaysEngineerAppointmentInfo/constants';
import { ANALYTICS_AMEND_APPT_NO, ANALYTICS_AMEND_APPT_YES, ANALYTICS_NO_APPOINTMENT_TODAY } from '../../constants';
import {
  AmendAndContactUsInfo,
  AmendAppointmentButton,
  AppointmentDescription,
  EngineerVisitSection,
  ReminderInfo,
  TodaysAppointmentCardContent,
} from './components';
import { CompensationLink } from './components/CompensationLink';
import { AMEND_DISCLAIMER, EXTERNAL_LINK, LINK_REDIRECTION_QUERY, NOTE } from './constants';

import styles from './styles.module.scss';

export const AppointmentCard: React.FC<components.AppointmentCard> = ({
  appointment,
  id,
  setAnalytics,
  isOpenreachApptToday,
  installationAddress,
}) => {
  const {
    orderItemReference,
    orderKey,
    type,
    title,
    reminderUrl,
    siteDetails,
    isKci2AssureOrder,
    email,
    engineeringAppointmentDate,
  } = appointment;
  let { status, formattedDate } = appointment;
  let cardContent;
  const amendedInfo = useSelector(getAmendsInfo)[orderItemReference];

  if (amendedInfo && amendedInfo.length > 0) {
    const [slotStart, slotEnd] = amendedInfo[1].value.split('-');
    formattedDate = `${amendedInfo[0].value}, starting between ${slotStart} and ${slotEnd}`;
  }
  const isAuthenticated = useIsAuthenticated();
  const isAmendAppointmentEnabled = useSelector(getIsAmendAppointmentEnabled);

  const { userName, userPhone } = siteDetails;

  const {
    hasAmendAppointmentButton,
    hasHelpSection: hasEngineerVisitSection,
    hasSiteContact,
    hasReminder,
    contactUsInfoType,
  } = AppointmentService.getAppointmentCardDetails(
    appointment,
    amendedInfo,
    isAuthenticated,
    isAmendAppointmentEnabled,
  );
  status = contactUsInfoType === CONTACT_US_INFO_TYPE.AlreadyAmended ? APPOINTMENT_STATUSES.Booked : status;
  const checkIsAmendAppointmentEnabled = () =>
    !(typeof isAmendAppointmentEnabled === 'boolean' && !isAmendAppointmentEnabled);

  const isStatusPONR = AppointmentService.checkPONRStatus(status, contactUsInfoType);

  const amendButtonText = AppointmentService.getAppointmentButtonText(
    status,
    type,
    checkIsAmendAppointmentEnabled(),
    isStatusPONR,
  );

  const showLoginSection =
    checkIsAmendAppointmentEnabled() && AppointmentService.showLoginSection(status) && !isAuthenticated;

  const showLoginButton = AppointmentService.showLoginButton(status, type, isStatusPONR);

  useEffect(() => {
    const storedStepIndex = localStorage.getItem(APPT_USER_PATH_STEP_STORAGE_NAME);
    const appointmentStatus = isKci2AssureOrder ? `${status} FTTP KCI2` : status;
    OPENREACH_APPOINTMENT_TYPES.includes(type) &&
      setAnalytics({
        appointmentStatus,
        amendPossible: hasAmendAppointmentButton ? ANALYTICS_AMEND_APPT_YES : ANALYTICS_AMEND_APPT_NO,
        appointmentStage: storedStepIndex
          ? MAP_CONTENT_BY_STEP[parseInt(storedStepIndex, 10)].analyticsEvent
          : ANALYTICS_NO_APPOINTMENT_TODAY,
      });
  }, []);

  if (isOpenreachApptToday === false) {
    cardContent = (
      <>
        <div className={styles.header}>
          <strong>{APPOINTMENT_TITLE[appointment.type] || appointment.type}</strong>
          {checkIsAmendAppointmentEnabled() && (
            <div className={styles.status}>
              {' '}
              <AppointmentStatusInfo statusText={status} status={status} isStatusPONR={isStatusPONR} />
            </div>
          )}
        </div>
        <div className={styles.addInfo}>
          <ClockIcon /> <div className={styles.text}>{formattedDate}</div>
        </div>
        {isAuthenticated && (
          <div className={styles.addInfo}>
            <HomeIcon /> <div className={styles.text}>{installationAddress}</div>
          </div>
        )}
        {contactUsInfoType === CONTACT_US_INFO_TYPE.AlreadyAmended && (
          <div className={styles.verticalSpace16}>
            <h5 className={styles.inline}>{NOTE}</h5> {AMEND_DISCLAIMER}
          </div>
        )}
        {hasSiteContact && isAuthenticated && (
          <div>
            <h5>Site contact</h5>
            <div className={styles.addInfo}>
              <ProfileIcon />
              <div className={styles.text}> {userName}</div>
            </div>
            <div className={styles.addInfo}>
              <PhoneIcon />
              <div className={styles.text}> {userPhone}</div>
            </div>
            <div className={styles.addInfo}>
              <EmailIcon />
              <div className={styles.text}> {email}</div>
            </div>
          </div>
        )}
        {hasReminder && <ReminderInfo reminderUrl={reminderUrl} title={title} />}
        <AmendAndContactUsInfo type={contactUsInfoType} isAmendAppointmentEnabled={checkIsAmendAppointmentEnabled()} />
        <AppointmentDescription
          status={status}
          isKci2AssureOrder={isKci2AssureOrder}
          amendedInfo={amendedInfo}
          isAmendAppointmentEnabled={checkIsAmendAppointmentEnabled()}
          isStatusPONR={isStatusPONR}
          type={type}
        />
        <CompensationLink status={status} />
        {showLoginSection && (
          <>
            <div className={styles.emptySpace} />
            <p>For security, we only show address and contact details when you log in.</p>
            {showLoginButton && (
              <a href={`${EXTERNAL_LINK}${LINK_REDIRECTION_QUERY}`}>
                <button type="button" className={styles.loginButton} title={getLoginButtonText(status, type)}>
                  {getLoginButtonText(status, type)}
                </button>
              </a>
            )}
          </>
        )}
        {isAuthenticated && (
          <AmendAppointmentButton
            itemId={orderItemReference}
            orderKey={orderKey}
            currentAppointmentDate={formattedDate}
            originAppointmentDate={engineeringAppointmentDate}
            type={type}
            text={amendButtonText}
          />
        )}
      </>
    );
  } else if (isOpenreachApptToday) {
    cardContent = <TodaysAppointmentCardContent formattedDate={formattedDate} />;
  } else {
    return null;
  }

  return (
    <>
      <div
        id={id}
        className={classNames(styles.cardContainer, { [styles.todaysApptInfoContainer]: isOpenreachApptToday })}
      >
        {cardContent}
      </div>
      {hasEngineerVisitSection && <EngineerVisitSection />}
    </>
  );
};
